import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Framework", target: "framework" },
  { label: "Services", target: "services" },
  { label: "Case Studies", target: "case-studies" },
  { label: "Signal Library", target: "signal-library" },
  { label: "About", target: "about" },
  { label: "Contact", target: "contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (target) => {
    setOpen(false);
    if (target === "contact") {
      navigate("/contact");
      return;
    }
    if (target === "signal-library") {
      if (location.pathname === "/") {
        document.getElementById("signal-library")?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/signal-library");
      }
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${target}`);
      setTimeout(() => {
        document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    } else {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-axiom-bg/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <span className="signal-dot animate-pulse-signal" />
            <span className="font-display text-xl md:text-2xl font-bold tracking-tight text-axiom-text">
              AXIOM-AI
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <button
                key={n.target}
                data-testid={`nav-link-${n.target}`}
                onClick={() => handleNav(n.target)}
                className="text-sm text-axiom-muted hover:text-axiom-text transition-colors duration-200 font-medium"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-request-review-btn"
              onClick={() => navigate("/contact")}
              className="btn-axiom text-sm hidden sm:inline-flex"
            >
              Request Review
              <span className="signal-dot" style={{ width: 6, height: 6 }} />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden text-axiom-text p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle navigation"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-axiom-bg/95 backdrop-blur-xl border-t border-white/5"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.target}
                data-testid={`nav-mobile-${n.target}`}
                onClick={() => handleNav(n.target)}
                className="text-left py-3 px-2 text-axiom-muted hover:text-axiom-text border-b border-white/5"
              >
                {n.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-request-review"
              onClick={() => {
                setOpen(false);
                navigate("/contact");
              }}
              className="btn-axiom text-sm justify-center mt-3"
            >
              Request Review
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
